import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  canSubmitReferrals,
  formatSuspendedUntil,
  getReferralAccessFromMembership,
  getReferralAccessMessage,
  getReferralAccessTooltip,
} from '../helpers/referralAccess';
import { selectCurrentUser } from '../selectors/authSelectors';
import { getUserOrganizationsMemoized, selectOrganizationLoading } from '../selectors/organizationSelectors';

function findMembership(userOrganizations, organization) {
  if (organization == null || organization === '') {
    return null;
  }

  const orgId = typeof organization === 'object' ? organization.id : organization;
  const identifier = typeof organization === 'object' ? organization.general_setting_identifier : undefined;

  return userOrganizations.find(({ organization: org }) => {
    if (orgId != null && orgId !== '' && Number(org?.id) === Number(orgId)) {
      return true;
    }

    if (identifier && org?.general_setting_identifier === identifier) {
      return true;
    }

    return false;
  });
}

const PENDING_ACCESS = {
  membership: null,
  referralAccess: null,
  canSubmitReferrals: false,
  blocked: true,
  pending: true,
  message: null,
  tooltipMessage: null,
  suspendedUntil: null,
};

export function useReferralAccess(organization) {
  const userOrganizations = useSelector(getUserOrganizationsMemoized);
  const membershipsFetched = useSelector(selectOrganizationLoading);
  const currentUser = useSelector(selectCurrentUser);
  const { t } = useTranslation('common');

  return useMemo(() => {
    const isAuthenticated = Boolean(currentUser);
    const membershipsPending = isAuthenticated && !membershipsFetched && userOrganizations.length === 0;
    const membershipsUnavailable = isAuthenticated && membershipsFetched && userOrganizations.length === 0;

    if (membershipsPending || membershipsUnavailable) {
      return PENDING_ACCESS;
    }

    const membership = findMembership(userOrganizations, organization);
    const referralAccess = getReferralAccessFromMembership(membership);
    const allowed = canSubmitReferrals(referralAccess);
    const message = getReferralAccessMessage(referralAccess, t);
    const tooltipMessage = getReferralAccessTooltip(referralAccess, t) || message;
    const suspendedUntil = formatSuspendedUntil(referralAccess);

    return {
      membership,
      referralAccess,
      canSubmitReferrals: allowed,
      blocked: !allowed,
      pending: false,
      message,
      tooltipMessage,
      suspendedUntil,
    };
  }, [currentUser, membershipsFetched, organization, t, userOrganizations]);
}
